import {
  BuildingEnergyModelFragment,
  CreateEnergySystemInput,
  EnergySystemFragment,
  EnergySystemFragmentDoc,
  UpdateEnergySystemInput,
  useCreateEnergySystemMutation,
  useUpdateEnergySystemMutation,
  useDeleteEnergySystemMutation,
} from '../generated/graphql'
import { useCacheHelpers } from '../apollo/cache-helpers'

type EnergySystemMutations = {
  isCreateLoading: boolean
  isUpdateLoading: boolean
  isDeleteLoading: boolean

  createEnergySystem: (
    buildingEnergyModel: BuildingEnergyModelFragment,
    input: CreateEnergySystemInput,
  ) => Promise<EnergySystemFragment>

  updateEnergySystem: (
    id: string,
    input: UpdateEnergySystemInput,
  ) => Promise<EnergySystemFragment>

  deleteEnergySystem: (
    buildingEnergyModel: BuildingEnergyModelFragment,
    id: string,
  ) => Promise<boolean>
}

export function useEnergySystems(): EnergySystemMutations {
  const [createEnergySystem, { loading: isCreateLoading }] =
    useCreateEnergySystemMutation()
  const [updateEnergySystem, { loading: isUpdateLoading }] =
    useUpdateEnergySystemMutation()
  const [deleteEnergySystem, { loading: isDeleteLoading }] =
    useDeleteEnergySystemMutation()
  const { addItemToList, removeItemFromList } = useCacheHelpers()

  return {
    isCreateLoading,
    isUpdateLoading,
    isDeleteLoading,

    async createEnergySystem(
      buildingEnergyModel: BuildingEnergyModelFragment,
      input: CreateEnergySystemInput,
    ) {
      const result = await createEnergySystem({
        variables: {
          buildingEnergyModelId: buildingEnergyModel.id,
          input,
        },

        update: (_, mutationResult) => {
          const energySystem = mutationResult.data?.createEnergySystem

          if (!energySystem) {
            return
          }

          addItemToList(
            EnergySystemFragmentDoc,
            energySystem,
            buildingEnergyModel,
            'energySystems',
          )
        },
      })

      if (!result.data) {
        throw new Error('Energy system creation failed')
      }

      return result.data.createEnergySystem
    },

    async updateEnergySystem(id: string, input: UpdateEnergySystemInput) {
      const result = await updateEnergySystem({ variables: { id, input } })

      if (!result.data) {
        throw new Error('Energy system update failed')
      }
      return result.data.updateEnergySystem
    },

    async deleteEnergySystem(
      buildingEnergyModel: BuildingEnergyModelFragment,
      id: string,
    ) {
      const result = await deleteEnergySystem({
        variables: { id },

        update: () => {
          removeItemFromList(
            EnergySystemFragmentDoc,
            id,
            buildingEnergyModel,
            'energySystems',
          )
        },

        optimisticResponse: {
          deleteEnergySystem: true,
        },
      })

      if (!result.data) {
        throw new Error('Energy system deletion failed')
      }

      return result.data.deleteEnergySystem
    },
  }
}
